// ไฟล์: frontend/src/pages/teacher/HealthRecords.js
import React, { useState, useEffect } from 'react';
import Header from '../../components/Header';
import Sidebar from '../../components/Sidebar';
import Footer from '../../components/Footer';
import api from '../../services/api';
import Swal from 'sweetalert2';
import { DEFAULT_AVATAR } from '../../assets/default_avatar';

function TeacherHealthRecords() {
  const [classes, setClasses] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [students, setStudents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  useEffect(() => {
    // เรียกข้อมูลชั้นเรียนที่ครูรับผิดชอบ
    const fetchClasses = async () => {
      try {
        const response = await api.class.getMyClasses();
        const myClasses = response.data.data || [];
        setClasses(myClasses);
        
        // เลือกชั้นเรียนแรกเป็นค่าเริ่มต้น
        if (myClasses.length > 0) {
          setSelectedClassId(myClasses[0].id);
        }
      } catch (error) {
        console.error('Error fetching classes:', error);
        Swal.fire({
          icon: 'error',
          title: 'เกิดข้อผิดพลาด',
          text: 'ไม่สามารถโหลดข้อมูลชั้นเรียนได้',
          confirmButtonColor: '#ff6b01'
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchClasses();
  }, []);
  
  useEffect(() => {
    // เรียกข้อมูลนักเรียนและข้อมูลการแปรงฟันและดื่มนมตามชั้นเรียนและวันที่ที่เลือก
    const fetchHealthRecords = async () => {
      if (!selectedClassId || !selectedDate) return;
      
      try {
        setIsLoading(true);
        const response = await api.healthRecord.getHealthRecordsByClassAndDate(selectedClassId, selectedDate);
        setStudents(response.data.data || []);
      } catch (error) {
        console.error('Error fetching health records:', error);
        Swal.fire({
          icon: 'error',
          title: 'เกิดข้อผิดพลาด',
          text: 'ไม่สามารถโหลดข้อมูลการแปรงฟันและดื่มนมได้',
          confirmButtonColor: '#ff6b01'
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchHealthRecords();
  }, [selectedClassId, selectedDate]);
  
  // บันทึกสถานะการแปรงฟัน
  const handleBrushTeethChange = async (studentId, brushTeeth) => {
    try {
      // หาข้อมูลการแปรงฟันและดื่มนมของนักเรียนเพื่อเตรียมอัปเดต
      const student = students.find(s => s.student_id === studentId);
      
      await api.healthRecord.recordHealthRecord({
        student_id: studentId,
        class_id: selectedClassId,
        record_date: selectedDate,
        brush_teeth: brushTeeth,
        drink_milk: student.drink_milk || false,
        note: student.note || ''
      });
      
      // อัปเดตสถานะในรายการนักเรียน
      setStudents(prevStudents => 
        prevStudents.map(student => 
          student.student_id === studentId 
            ? { ...student, brush_teeth: brushTeeth } 
            : student
        )
      );
      
      // แสดงข้อความสำเร็จเล็กๆ ด้านมุมขวาบน
      const Toast = Swal.mixin({
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 1500,
        timerProgressBar: true,
        didOpen: (toast) => {
          toast.addEventListener('mouseenter', Swal.stopTimer);
          toast.addEventListener('mouseleave', Swal.resumeTimer);
        }
      });
      
      Toast.fire({
        icon: 'success',
        title: 'บันทึกสถานะการแปรงฟันเรียบร้อย'
      });
    } catch (error) {
      console.error('Error recording brush teeth status:', error);
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: 'ไม่สามารถบันทึกสถานะการแปรงฟันได้',
        confirmButtonColor: '#ff6b01'
      });
    }
  };
  
  // บันทึกสถานะการดื่มนม
  const handleDrinkMilkChange = async (studentId, drinkMilk) => {
    try {
      // หาข้อมูลการแปรงฟันและดื่มนมของนักเรียนเพื่อเตรียมอัปเดต
      const student = students.find(s => s.student_id === studentId);
      
      await api.healthRecord.recordHealthRecord({
        student_id: studentId,
        class_id: selectedClassId,
        record_date: selectedDate,
        brush_teeth: student.brush_teeth || false,
        drink_milk: drinkMilk,
        note: student.note || ''
      });
      
      // อัปเดตสถานะในรายการนักเรียน
      setStudents(prevStudents => 
        prevStudents.map(student => 
          student.student_id === studentId 
            ? { ...student, drink_milk: drinkMilk } 
            : student
        )
      );
      
      // แสดงข้อความสำเร็จเล็กๆ ด้านมุมขวาบน
      const Toast = Swal.mixin({
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 1500,
        timerProgressBar: true,
        didOpen: (toast) => {
          toast.addEventListener('mouseenter', Swal.stopTimer);
          toast.addEventListener('mouseleave', Swal.resumeTimer);
        }
      });
      
      Toast.fire({
        icon: 'success',
        title: 'บันทึกสถานะการดื่มนมเรียบร้อย'
      });
    } catch (error) {
      console.error('Error recording drink milk status:', error);
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: 'ไม่สามารถบันทึกสถานะการดื่มนมได้',
        confirmButtonColor: '#ff6b01'
      });
    }
  };
  
  // บันทึกหมายเหตุ
  const handleNoteChange = async (studentId, note) => {
    try {
      // หาข้อมูลการแปรงฟันและดื่มนมของนักเรียนเพื่อเตรียมอัปเดต
      const student = students.find(s => s.student_id === studentId);
      
      await api.healthRecord.recordHealthRecord({
        student_id: studentId,
        class_id: selectedClassId,
        record_date: selectedDate,
        brush_teeth: student.brush_teeth || false,
        drink_milk: student.drink_milk || false,
        note: note
      });
      
      // อัปเดตหมายเหตุในรายการนักเรียน
      setStudents(prevStudents => 
        prevStudents.map(student => 
          student.student_id === studentId 
            ? { ...student, note: note } 
            : student
        )
      );
      
      // แสดงข้อความสำเร็จเล็กๆ ด้านมุมขวาบน
      const Toast = Swal.mixin({
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 1500,
        timerProgressBar: true,
        didOpen: (toast) => {
          toast.addEventListener('mouseenter', Swal.stopTimer);
          toast.addEventListener('mouseleave', Swal.resumeTimer);
        }
      });
      
      Toast.fire({
        icon: 'success',
        title: 'บันทึกหมายเหตุเรียบร้อย'
      });
    } catch (error) {
      console.error('Error recording note:', error);
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: 'ไม่สามารถบันทึกหมายเหตุได้',
        confirmButtonColor: '#ff6b01'
      });
    }
  };
  
  // บันทึกทั้งหมดเป็นแปรงฟันและดื่มนม
  const handleMarkAllComplete = async () => {
    try {
      setIsLoading(true);
      
      // ยืนยันการบันทึกทั้งหมด
      const result = await Swal.fire({
        title: 'ยืนยันการบันทึก',
        text: 'ยืนยันการบันทึกว่านักเรียนทั้งหมดแปรงฟันและดื่มนมแล้ว?',
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#ff6b01',
        cancelButtonColor: '#6c757d',
        confirmButtonText: 'ยืนยัน',
        cancelButtonText: 'ยกเลิก'
      });
      
      if (result.isConfirmed) {
        // บันทึกสถานะสำหรับนักเรียนทุกคน
        const promises = students.map(student => 
          api.healthRecord.recordHealthRecord({
            student_id: student.student_id,
            class_id: selectedClassId,
            record_date: selectedDate,
            brush_teeth: true,
            drink_milk: true,
            note: student.note || ''
          })
        );
        
        await Promise.all(promises);
        
        // อัปเดตสถานะในรายการนักเรียน
        setStudents(prevStudents => 
          prevStudents.map(student => ({
            ...student,
            brush_teeth: true,
            drink_milk: true
          }))
        );
        
        Swal.fire({
          icon: 'success',
          title: 'บันทึกสำเร็จ',
          text: 'บันทึกสถานะการแปรงฟันและดื่มนมทั้งหมดเรียบร้อยแล้ว',
          confirmButtonColor: '#ff6b01',
          timer: 1500,
          showConfirmButton: false
        });
      }
    } catch (error) {
      console.error('Error marking all complete:', error);
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: 'ไม่สามารถบันทึกสถานะทั้งหมดได้',
        confirmButtonColor: '#ff6b01'
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  // สรุปจำนวนสถานะการแปรงฟันและดื่มนม
  const getHealthRecordSummary = () => {
    const summary = {
      brush_teeth: 0,
      drink_milk: 0,
      both: 0,
      none: 0,
      total: students.length
    };
    
    students.forEach(student => {
      if (student.brush_teeth && student.drink_milk) {
        summary.both++;
      } else if (student.brush_teeth) {
        summary.brush_teeth++;
      } else if (student.drink_milk) {
        summary.drink_milk++;
      } else {
        summary.none++;
      }
    });
    
    return summary;
  };
  
  const summary = getHealthRecordSummary();
  
  return (
    <div className="wrapper">
      <Header />
      <Sidebar />
      
      <div className="content-wrapper">
        <div className="content-header">
          <div className="container-fluid">
            <div className="row mb-2">
              <div className="col-sm-6">
                <h1 className="m-0">บันทึกการแปรงฟันและดื่มนม</h1>
              </div>
              <div className="col-sm-6">
                <ol className="breadcrumb float-sm-right">
                  <li className="breadcrumb-item">
                    <a href="/teacher/dashboard">หน้าหลัก</a>
                  </li>
                  <li className="breadcrumb-item active">แปรงฟันและดื่มนม</li>
                </ol>
              </div>
            </div>
          </div>
        </div>
        
        <section className="content">
          <div className="container-fluid">
            {isLoading ? (
              <div className="loading-spinner">
                <i className="fas fa-spinner fa-spin"></i>
              </div>
            ) : (
              <>
                {/* ตัวเลือกชั้นเรียนและวันที่ */}
                <div className="row">
                  <div className="col-12">
                    <div className="card card-orange">
                      <div className="card-header">
                        <h3 className="card-title">เลือกชั้นเรียนและวันที่</h3>
                      </div>
                      <div className="card-body">
                        {classes.length === 0 ? (
                          <div className="alert alert-warning">
                            <i className="fas fa-exclamation-triangle mr-2"></i>
                            คุณยังไม่ได้รับมอบหมายให้ดูแลชั้นเรียนใดๆ กรุณาติดต่อผู้ดูแลระบบ
                          </div>
                        ) : (
                          <div className="row">
                            <div className="col-md-6">
                              <div className="form-group">
                                <label>ชั้นเรียน</label>
                                <select
                                  className="form-control"
                                  value={selectedClassId}
                                  onChange={(e) => setSelectedClassId(e.target.value)}
                                >
                                  {classes.map(classItem => (
                                    <option key={classItem.id} value={classItem.id}>
                                      {classItem.class_name} (ห้อง {classItem.room_number})
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>
                            <div className="col-md-6">
                              <div className="form-group">
                                <label>วันที่</label>
                                <input
                                  type="date"
                                  className="form-control"
                                  value={selectedDate}
                                  onChange={(e) => setSelectedDate(e.target.value)}
                                />
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* รายการนักเรียนและการแปรงฟันและดื่มนม */}
                {classes.length > 0 && (
                  <div className="row">
                    <div className="col-12">
                      <div className="card card-orange">
                        <div className="card-header">
                          <h3 className="card-title">รายการนักเรียนและการแปรงฟันและดื่มนม</h3>
                          <div className="card-tools">
                            <button
                              type="button"
                              className="btn btn-tool"
                              data-card-widget="collapse"
                            >
                              <i className="fas fa-minus"></i>
                            </button>
                          </div>
                        </div>
                        <div className="card-body">
                          {/* สรุปการแปรงฟันและดื่มนม */}
                          <div className="row">
                            <div className="col-12 col-md-3">
                              <div className="info-box bg-success">
                                <div className="info-box-content text-center">
                                  <span className="info-box-text">แปรงฟันและดื่มนม</span>
                                  <span className="info-box-number">{summary.both} คน</span>
                                </div>
                              </div>
                            </div>
                            <div className="col-12 col-md-3">
                              <div className="info-box bg-info">
                                <div className="info-box-content text-center">
                                  <span className="info-box-text">แปรงฟันอย่างเดียว</span>
                                  <span className="info-box-number">{summary.brush_teeth} คน</span>
                                </div>
                              </div>
                            </div>
                            <div className="col-12 col-md-3">
                              <div className="info-box bg-warning">
                                <div className="info-box-content text-center">
                                  <span className="info-box-text">ดื่มนมอย่างเดียว</span>
                                  <span className="info-box-number">{summary.drink_milk} คน</span>
                                </div>
                              </div>
                            </div>
                            <div className="col-12 col-md-3">
                              <div className="info-box bg-danger">
                                <div className="info-box-content text-center">
                                  <span className="info-box-text">ยังไม่แปรงฟันและดื่มนม</span>
                                  <span className="info-box-number">{summary.none} คน</span>
                                </div>
                              </div>
                            </div>
                          </div>
                          
                          {/* ปุ่มบันทึกทั้งหมด */}
                          <div className="row mb-3">
                            <div className="col-12">
                              <button
                                type="button"
                                className="btn btn-success"
                                onClick={handleMarkAllComplete}
                              >
                                <i className="fas fa-check-double mr-2"></i>
                                บันทึกว่าทุกคนแปรงฟันและดื่มนมแล้ว
                              </button>
                            </div>
                          </div>
                          
                          {/* รายการนักเรียน */}
                          {students.length === 0 ? (
                            <div className="alert alert-info">
                              <i className="fas fa-info-circle mr-2"></i>
                              ไม่พบข้อมูลนักเรียนในชั้นเรียนนี้
                            </div>
                          ) : (
                            <div className="table-responsive">
                              <table className="table table-bordered table-striped">
                                <thead>
                                  <tr>
                                    <th style={{ width: '80px' }}>รูปภาพ</th>
                                    <th style={{ width: '80px' }}>รหัส</th>
                                    <th>ชื่อ-นามสกุล</th>
                                    <th style={{ width: '120px' }}>แปรงฟัน</th>
                                    <th style={{ width: '120px' }}>ดื่มนม</th>
                                    <th style={{ width: '200px' }}>หมายเหตุ</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {students.map((student) => (
                                    <tr key={student.student_id}>
                                      <td>
                                        <img
                                          src={`https://cms2568.backend.kruwirat.com/uploads/${student.profile_pic}`}
                                          alt={`${student.first_name} ${student.last_name}`}
                                          className="student-profile-pic-sm"
                                          onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.src = DEFAULT_AVATAR;
                                          }}
                                        />
                                      </td>
                                      <td>{student.student_code}</td>
                                      <td>
                                        {student.first_name} {student.last_name}
                                      </td>
                                      <td>
                                        <div className="custom-control custom-switch">
                                          <input
                                            type="checkbox"
                                            className="custom-control-input"
                                            id={`brush-teeth-${student.student_id}`}
                                            checked={student.brush_teeth || false}
                                            onChange={(e) => handleBrushTeethChange(student.student_id, e.target.checked)}
                                          />
                                          <label className="custom-control-label" htmlFor={`brush-teeth-${student.student_id}`}>
                                            {student.brush_teeth ? 'แปรงแล้ว' : 'ยังไม่แปรง'}
                                          </label>
                                        </div>
                                      </td>
                                      <td>
                                        <div className="custom-control custom-switch">
                                          <input
                                            type="checkbox"
                                            className="custom-control-input"
                                            id={`drink-milk-${student.student_id}`}
                                            checked={student.drink_milk || false}
                                            onChange={(e) => handleDrinkMilkChange(student.student_id, e.target.checked)}
                                          />
                                          <label className="custom-control-label" htmlFor={`drink-milk-${student.student_id}`}>
                                            {student.drink_milk ? 'ดื่มแล้ว' : 'ยังไม่ดื่ม'}
                                          </label>
                                        </div>
                                      </td>
                                      <td>
                                        <input
                                          type="text"
                                          className="form-control form-control-sm"
                                          placeholder="หมายเหตุ"
                                          value={student.note || ''}
                                          onChange={(e) => handleNoteChange(student.student_id, e.target.value)}
                                          onBlur={(e) => {
                                            if (e.target.value !== student.note) {
                                              handleNoteChange(student.student_id, e.target.value);
                                            }
                                          }}
                                        />
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      </div>
      
      <Footer />
    </div>
  );
}

export default TeacherHealthRecords;